import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useCurrency, RATES } from "@/contexts/CurrencyContext";

// Fix default marker icons in webpack/CRA bundling
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Branded SVG marker for our companions (orange teardrop)
const companionIcon = L.divIcon({
  className: "companion-marker",
  html: `<div style="
    width: 32px; height: 32px;
    background: #C05640;
    border: 3px solid #fff;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    box-shadow: 0 4px 10px rgba(0,0,0,0.25);
    display: grid; place-items: center;
  "><div style="
    width: 10px; height: 10px;
    background: #fff;
    border-radius: 50%;
    transform: rotate(45deg);
  "></div></div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Pulsing red pin for the agreed meeting point
const meetIcon = L.divIcon({
  className: "meet-marker",
  html: `<div style="position:relative;width:34px;height:34px;">
    <div style="
      position:absolute;inset:0;
      background:#DC2626;
      border:3px solid #fff;
      border-radius:50% 50% 50% 0;
      transform:rotate(-45deg);
      box-shadow:0 4px 12px rgba(220,38,38,0.45);
      display:grid;place-items:center;
    ">
      <div style="
        width:11px;height:11px;
        background:#fff;
        border-radius:50%;
        transform:rotate(45deg);
      "></div>
    </div>
    <div style="
      position:absolute;inset:-6px;
      border:2px solid #DC2626;
      border-radius:50%;
      opacity:0.5;
      animation:meetPulse 1.6s ease-out infinite;
    "></div>
    <style>@keyframes meetPulse{0%{transform:scale(0.7);opacity:0.7}100%{transform:scale(1.4);opacity:0}}</style>
  </div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 34],
  popupAnchor: [0, -34],
});

function FlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, zoom || map.getZoom(), { duration: 0.8 });
    }
  }, [center, zoom, map]);
  return null;
}

/**
 * MapPreview — reusable map for both Discover and live-location/meet-point.
 *
 * @param interactive  When true (default for Discover), pin popups show a
 *                     "View profile" link. Set false on the booking page so
 *                     fake "my-pin"/"their-pin" provider_ids don't route to
 *                     `/providers/their-pin` (404 → orphan Loading screen).
 * @param meetPoint    Optional { lat, lng, label } for the agreed meeting
 *                     point. Renders a pulsing red marker.
 */
export default function MapPreview({
  center,
  providers = [],
  meetPoint = null,
  height = 320,
  interactive = true,
}) {
  const { currency, symbol } = useCurrency();
  const fx = RATES[currency]?.rate || 1;
  if (!center) return null;
  const zoom = 13;

  return (
    <div
      className="rounded-2xl overflow-hidden border border-stone-200 shadow-sm"
      data-testid="map-preview"
      style={{ height }}
    >
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={zoom}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FlyTo center={[center.lat, center.lng]} zoom={zoom} />
        {providers.map((p) => (
          <Marker
            key={p.provider_id}
            position={[p.lat, p.lng]}
            icon={companionIcon}
          >
            <Popup>
              <div style={{ minWidth: 180 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {p.name}{p.age ? ` · ${p.age}` : ""}
                </div>
                <div style={{ fontSize: 12, color: "#78716C", marginTop: 2 }}>
                  {p.city}{typeof p.hourly_rate === "number" && p.hourly_rate > 0
                    ? ` · ${symbol}${Math.round(p.hourly_rate * fx)}/hr`
                    : ""}
                </div>
                {interactive && (
                  <a
                    href={`/providers/${p.provider_id}`}
                    style={{
                      display: "inline-block",
                      marginTop: 6,
                      color: "#4A5D4E",
                      fontWeight: 600,
                      fontSize: 12,
                      textDecoration: "underline",
                    }}
                  >
                    View profile
                  </a>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
        {meetPoint && meetPoint.lat && meetPoint.lng && (
          <Marker position={[meetPoint.lat, meetPoint.lng]} icon={meetIcon}>
            <Popup>
              <div style={{ minWidth: 180 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#DC2626" }}>
                  📍 Meeting point
                </div>
                {meetPoint.label && (
                  <div style={{ fontSize: 12, color: "#0F172A", marginTop: 4, fontWeight: 500 }}>
                    {meetPoint.label}
                  </div>
                )}
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${meetPoint.lat},${meetPoint.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-block",
                    marginTop: 6,
                    color: "#4A5D4E",
                    fontWeight: 600,
                    fontSize: 12,
                    textDecoration: "underline",
                  }}
                >
                  Open in Google Maps
                </a>
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
}
